import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Plus, Users, Edit2, Trash2, Building, Phone, Mail } from 'lucide-react';
import { COMPANY } from '../../../config/company';

// Contact role configuration
const CONTACT_ROLES = {
  homeowner: { label: 'Homeowner', labelEs: 'Propietario', color: '#3b82f6' },
  project_manager: { label: 'Project Manager', labelEs: 'Gerente de Proyecto', color: '#8b5cf6' },
  architect: { label: 'Architect', labelEs: 'Arquitecto', color: '#6366f1' },
  engineer: { label: 'Engineer', labelEs: 'Ingeniero', color: '#14b8a6' },
  inspector: { label: 'Inspector', labelEs: 'Inspector', color: '#f59e0b' },
  hoa_rep: { label: 'HOA Representative', labelEs: 'Representante HOA', color: '#ec4899' },
  tenant: { label: 'Tenant', labelEs: 'Inquilino', color: '#06b6d4' },
  property_manager: { label: 'Property Manager', labelEs: 'Administrador', color: '#10b981' },
  insurance_adjuster: { label: 'Insurance Adjuster', labelEs: 'Ajustador de Seguros', color: '#f97316' },
  vendor: { label: 'Vendor', labelEs: 'Proveedor', color: '#84cc16' },
  subcontractor: { label: 'Subcontractor', labelEs: 'Subcontratista', color: '#22c55e' },
  other: { label: 'Other', labelEs: 'Otro', color: '#6b7280' }
};

/**
 * ClientContactsTab - Displays and manages project contacts
 */
const ClientContactsTab = ({
  language,
  contacts,
  openNewContactModal,
  openEditContactModal,
  deleteContact
}) => {
  return (
    <Card className="bg-white border-gray-200">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-gray-900">
            {language === 'es' ? 'Contactos del Proyecto' : 'Project Contacts'}
          </CardTitle>
          <Button 
            onClick={openNewContactModal} 
            style={{ backgroundColor: COMPANY.primaryColor }} 
            className="text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            {language === 'es' ? 'Agregar Contacto' : 'Add Contact'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {contacts.length === 0 ? (
          <div className="text-center py-8">
            <Users className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-4">
              {language === 'es' 
                ? 'No hay contactos para este proyecto'
                : 'No contacts for this project yet'}
            </p>
            <Button onClick={openNewContactModal} variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              {language === 'es' ? 'Agregar Primer Contacto' : 'Add First Contact'}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {contacts.map(contact => {
              const roleConfig = CONTACT_ROLES[contact.role] || CONTACT_ROLES.other;
              return (
                <div 
                  key={contact.id} 
                  className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                        style={{ backgroundColor: roleConfig.color }}
                      >
                        {contact.name?.charAt(0).toUpperCase() || '?'}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{contact.name}</h4>
                        <Badge 
                          variant="outline" 
                          className="text-xs"
                          style={{ borderColor: roleConfig.color, color: roleConfig.color }}
                        >
                          {language === 'es' ? roleConfig.labelEs : roleConfig.label}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEditContactModal(contact)}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-600" 
                        onClick={() => deleteContact(contact.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-1 text-sm">
                    {contact.company && (
                      <p className="text-gray-600 flex items-center">
                        <Building className="h-3 w-3 mr-2 text-gray-400" />
                        {contact.company}
                      </p>
                    )}
                    {contact.phone && (
                      <p className="text-gray-600 flex items-center">
                        <Phone className="h-3 w-3 mr-2 text-gray-400" />
                        <a href={`tel:${contact.phone}`} className="hover:text-amber-600">
                          {contact.phone}
                        </a>
                      </p>
                    )}
                    {contact.mobile && (
                      <p className="text-gray-600 flex items-center">
                        <Phone className="h-3 w-3 mr-2 text-gray-400" />
                        <a href={`tel:${contact.mobile}`} className="hover:text-amber-600">
                          {contact.mobile}
                        </a>
                        <span className="text-xs text-gray-400 ml-1">(mobile)</span>
                      </p>
                    )}
                    {contact.email && (
                      <p className="text-gray-600 flex items-center">
                        <Mail className="h-3 w-3 mr-2 text-gray-400" />
                        <a href={`mailto:${contact.email}`} className="hover:text-amber-600">
                          {contact.email}
                        </a>
                      </p>
                    )}
                  </div>
                  {contact.notes && (
                    <p className="mt-2 text-xs text-gray-500 bg-gray-50 p-2 rounded">
                      {contact.notes}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export { CONTACT_ROLES };
export default ClientContactsTab;
